import React from 'react';
import request from 'arcdynamic-request';
import Dropdown from "../Dropdown/Dropdown";
import PropTypes from "prop-types";
import { formatName } from "../../utils";
import { TEAM } from '../../consts/tables';
import { getData } from '../../requests/CRUD';
import { buildSummitAgentQuery, compareByFirstName, compareByLastName, isInSummitAgentPool, mergeAgentOptions } from './agentPool';

const limit = 1000;

class SelectTeam extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            items: [],
            isLoading: false,
            searchTerm: "",
            inputValue: "",
            selectedOptions: [],
        };

        this.activeRequestId = 0;

        this.fetchAll = this.fetchAll.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
        this.handleSelectedOptionsChange = this.handleSelectedOptionsChange.bind(this);
        this.syncSelectedOptionsFromValue = this.syncSelectedOptionsFromValue.bind(this);
        this.ensureSelectedOption = this.ensureSelectedOption.bind(this);
        this.getOptionLabel = this.getOptionLabel.bind(this);
    }

    componentDidMount() {
        this.fetchAll();
        this.ensureSelectedOption(this.props.value);
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.value !== this.props.value) {
            this.ensureSelectedOption(this.props.value);
        }

        if (prevProps.value !== this.props.value || prevState.items !== this.state.items) {
            this.syncSelectedOptionsFromValue();
        }
    }

    componentWillUnmount() {
        this.activeRequestId++;
    }

    extractId(option) {
        if (!option) return null;
        if (typeof option === 'object') {
            if (option.ARCIMEDES_ROW_ID !== undefined) return String(option.ARCIMEDES_ROW_ID);
            if (option.value !== undefined) return String(option.value);
        }
        return option !== undefined && option !== null ? String(option) : null;
    }

    getSelectedIdsFromValue() {
        const { value } = this.props;
        if (value === null || value === undefined) return [];
        if (Array.isArray(value)) return value.map((v) => String(this.extractId(v))).filter(Boolean);
        if (typeof value === 'object') {
            const id = this.extractId(value);
            return id ? [id] : [];
        }
        return String(value).split(',').map(v => v.trim()).filter(Boolean);
    }

    syncSelectedOptionsFromValue() {
        const selectedIds = this.getSelectedIdsFromValue();
        if (!selectedIds.length) {
            if (this.state.selectedOptions.length) {
                this.setState({ selectedOptions: [] });
            }
            return;
        }

        const map = new Map();
        this.state.selectedOptions.forEach(opt => {
            const id = this.extractId(opt);
            if (id) map.set(id, opt);
        });
        this.state.items.forEach(opt => {
            const id = this.extractId(opt);
            if (id && selectedIds.includes(id)) {
                map.set(id, opt);
            }
        });

        const next = selectedIds.map(id => map.get(id)).filter(Boolean);
        const nextState = { selectedOptions: next };
        // Single-select shows the choice as the input text. Multi-select
        // already renders it as a chip — mirroring it into the input printed
        // the name twice and pre-filtered the option list to just that name.
        if (!this.state.searchTerm && next.length === 1 && this.props.dropdownType !== 'multiple') {
            const label = this.getOptionLabel(next[0]);
            if (label && this.state.inputValue !== label) {
                nextState.inputValue = label;
            }
        }
        this.setState(nextState);
    }

    ensureSelectedOption(value) {
        const id = this.extractId(value);
        if (!id) return;

        const alreadyThere = this.state.items.find(item => this.extractId(item) === id);
        if (alreadyThere) return;

        this.setState({ isLoading: true });

        getData({
            tableCode: TEAM,
            options: {
                limit: { count: 1, offset: 0 },
                column: [
                    'ARCIMEDES_ROW_ID',
                    'FIRST_NAME',
                    'LAST_NAME',
                    'DELETED',
                    'IS_AGENT',
                ],
                filter: [
                    {
                        code: 'ARCIMEDES_ROW_ID',
                        text: id,
                        type: 'equal',
                    }
                ],
                filterType: 'and',
            },
        }).then(res => {
            if (res && res.success && Array.isArray(res.data) && res.data.length) {
                this.setState((prev) => ({
                    items: [res.data[0], ...prev.items],
                }), this.syncSelectedOptionsFromValue);
            }
        }).catch(() => {});
    }

    fetchAll() {
        const requestId = ++this.activeRequestId;
        let filter = [];
        let filterType;
        if (this.props.onlySummitAgents) {
            // Listing/Buyer agent pool = Summit Agents OR Transaction Coordinators
            // (Bonnie, Anna S). (Claritask 545619 / 546632)
            const summitAgentQuery = buildSummitAgentQuery();
            filter = summitAgentQuery.filter;
            filterType = summitAgentQuery.filterType;
        } else {
            if (this.props.onlySummitEmployees) {
                filter.push({ code: 'IS_AGENT', text: 'e', type: 'exact' });
            }
            if (this.props.transactionCoordinator) {
                filter.push({ code: 'TITLE', text: 'Transaction Coordinator', type: 'contains' });
            }
            if (filter.length) filterType = 'and';
        }
        getData({
            tableCode: TEAM,
            options: {
                limit: {
                    count: limit,
                    offset: 0,
                },
                column: [
                    'ARCIMEDES_ROW_ID',
                    'FIRST_NAME',
                    'LAST_NAME',
                    'DELETED',
                    'IS_AGENT',
                    'TITLE',
                    'TERMINATION_DATE',
                ],
                filter,
                ...(filterType && { filterType }),
                order: [
                    {
                        code: 'LAST_NAME',
                        type: 'asc',
                    },
                    {
                        code: 'FIRST_NAME',
                        type: 'asc',
                    },
                ],
            },
        }).then(res => {
            if (requestId !== this.activeRequestId) return;

            let newItems = res && res.data ? res.data.filter(x => x.FIRST_NAME && x.LAST_NAME) : [];
            if (this.props.onlySummitAgents) {
                // Re-apply the pool rule client-side: Arc silently drops filters it
                // doesn't support, so never trust the query alone to have narrowed it.
                newItems = newItems.filter(isInSummitAgentPool).filter(x => !x.TERMINATION_DATE);
            }
            this.setState({
                items: newItems,
                isLoading: false,
                searchTerm: "",
            });
        }).catch((err) => {
            if (requestId !== this.activeRequestId) return;
            console.error("Error fetching team members:", err);
            this.setState({ isLoading: false });
        });

        this.setState({ isLoading: true });
    }

    handleInputChange(event, newValue, reason) {
        if (reason === "reset") return;
        if (reason === "clear") {
            this.setState({ inputValue: "" });
            return;
        }

        const next = (newValue || "").trimStart();
        this.setState({ inputValue: next });
    }

    getOptionLabel(option) {
        if (!option || typeof option !== 'object') return '';
        return formatName(option) || '';
    }

    handleSelectedOptionsChange(options) {
        const nextOptions = Array.isArray(options) ? options.filter(Boolean) : (options ? [options] : []);
        const map = new Map();
        nextOptions.forEach(opt => {
            const id = this.extractId(opt);
            if (id) map.set(id, opt);
        });
        const selectedOptions = Array.from(map.values());
        const nextState = { selectedOptions };
        // See syncSelectedOptionsFromValue: never echo a chip into the input.
        if (selectedOptions.length === 1 && this.props.dropdownType !== 'multiple') {
            const label = this.getOptionLabel(selectedOptions[0]);
            if (label) {
                nextState.inputValue = label;
                nextState.searchTerm = "";
            }
        }
        if (selectedOptions.length === 0 || this.props.dropdownType === 'multiple') {
            nextState.inputValue = "";
            nextState.searchTerm = "";
        }
        this.setState(nextState);
    }

    render() {
        const {
            theme,
            value,
            valueKey,
            percentKey,
            label,
            percentLabel,
            disabled,
            data,
            isFilter,
            handleParentChange,
            handleInputParentChange,
            viewMode,
            deleted,
            isFromProperty,
            hasAgentPercent,
            setParentState,
            ...rest
        } = this.props;
        const { items, isLoading, inputValue } = this.state;
        const inactive = items && items.filter(x => x.DELETED === 'y') || [];
        const active = items && items.filter(x => x.DELETED !== 'y') || [];
        let itemsSorted = [...active, ...inactive];

        itemsSorted = itemsSorted.map((el) => {
            return {
                ...el,
                groupedBy: el.DELETED !== 'y' ? 'active' : 'inactive'
            };
        });

        if(this.props.onlyActive){
            itemsSorted = itemsSorted.filter((e)=> e.IS_AGENT != 'n' && e.groupedBy==='active')
        }

        // Unique by id, selected first, every option stamped with the group the
        // dropdown groups on — see mergeAgentOptions. (Claritask 546632)
        let mergedOptions = mergeAgentOptions(
            this.state.selectedOptions,
            itemsSorted,
            (opt) => this.extractId(opt),
        );
        // Listing/Buyer agent dropdowns: order alphabetically by last name (or
        // first name where the page asks for it), after the merge so an
        // already-selected option (prepended above) also lands in alphabetical
        // position rather than pinned to the top. Keep active before inactive
        // so grouping stays intact. (Claritask 546632)
        if (this.props.onlySummitAgents) {
            const compare = this.props.sortByFirstName ? compareByFirstName : compareByLastName;
            mergedOptions.sort((a, b) => {
                const ai = a && a.DELETED === 'y' ? 1 : 0;
                const bi = b && b.DELETED === 'y' ? 1 : 0;
                return ai !== bi ? ai - bi : compare(a, b);
            });
        }

        return (
            <Dropdown theme={theme}
                      optionsType='team'
                      optionKey='ARCIMEDES_ROW_ID'
                      options={mergedOptions}
                      value={value}
                      valueKey={valueKey}
                      percentKey={percentKey}
                      label={label}
                      percentLabel={percentLabel}
                      disabled={disabled}
                      data={data}
                      isFilter={isFilter}
                      groupBy={true && !this.props.onlyActive}
                      handleParentChange={handleParentChange}
                      handleInputParentChange={handleInputParentChange}
                      viewMode={viewMode}
                      deleted={deleted}
                      isFromProperty={isFromProperty}
                      hasAgentPercent={hasAgentPercent}
                      disableClientFilter={false}
                      onInputChange={this.handleInputChange}
                      inputValue={inputValue}
                      loading={isLoading}
                      loadingText="Loading team..."
                      noOptionsText={inputValue ? "No matching team members" : "No team members available"}
                      onSelectedOptionsChange={this.handleSelectedOptionsChange}
                      {...rest}
            />
        )
    }
}

SelectTeam.propTypes = {
    theme: PropTypes.oneOf(['default', 'white', 'dark']),
    value: PropTypes.string,
    valueKey: PropTypes.string.isRequired,
    label: PropTypes.string,
    percentLabel: PropTypes.string,
    disabled: PropTypes.bool,
    data: PropTypes.object,
    handleParentChange: PropTypes.func.isRequired,
    isFilter: PropTypes.bool,
    viewMode: PropTypes.bool,
    groupBy: PropTypes.bool,
    deleted: PropTypes.bool,
    isFromProperty: PropTypes.bool,
    hasAgentPercent: PropTypes.bool,
    handleInputParentChange: PropTypes.func,
    onlySummitAgents: PropTypes.bool,
    sortByFirstName: PropTypes.bool,
};

export default SelectTeam;
